/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Cart, CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { IITSIntegrationCartLine } from '../custom-actions/DataServiceEntities.g';
import { getCartData, setCartData } from './cookieutils';
import { orderAllergensChange, readOrderAllergensValue } from './orderallergens';
import { orderNoteChange, readOrderNote } from './ordernote';
import { withoutStoreLineFacts } from './orderstate';

/** A line of the order as the store held it when the order was last read. */
export interface ISentLine {
    ItemId?: string;
    ProductId?: number;
    LineId?: string;
    Quantity?: number;
    UnitOfMeasureSymbol?: string;
    Comments?: string;
}

export interface IOrderChanges {
    AddCartLines: IITSIntegrationCartLine[];
    UpdateCartLines: IITSIntegrationCartLine[];
    RemoveCartLines: IITSIntegrationCartLine[];
    ExtensionProperties: CommerceProperty[];
}

/** The lines the store held when this cart's order was last read, or undefined where none were recorded. */
export const readSentLines = (cartId: string | undefined): ISentLine[] | undefined => {
    const handshake = getCartData();

    if (!cartId || handshake?.CartId !== cartId || !Array.isArray(handshake?.cartLines)) {
        return undefined;
    }

    return handshake.cartLines as ISentLine[];
};

/** The header values the store held when the order was last read, which an update sends only where the guest changed them. */
export interface IHeldOrderHeader {
    Note: string;
    Allergens: string;
}

export const heldOrderHeaderOf = (cart: Cart): IHeldOrderHeader => ({
    Note: readOrderNote(cart),
    Allergens: readOrderAllergensValue(cart)
});

/** The header values the store held when this cart's order was last read, or undefined where none were recorded. */
export const readHeldOrderHeader = (cartId: string | undefined): IHeldOrderHeader | undefined => {
    const handshake = getCartData();

    return cartId && handshake?.CartId === cartId ? (handshake.heldOrderHeader as IHeldOrderHeader | undefined) : undefined;
};

/** Records the lines of the store's copy as the lines the store holds, on the handshake for that cart. */
export const recordSentLines = (cart: Cart): void => {
    const handshake = getCartData();

    if (!cart.Id || handshake?.CartId !== cart.Id) {
        return;
    }

    const cartLines: ISentLine[] = (cart.CartLines ?? []).map(line => ({
        ItemId: line.ItemId,
        ProductId: line.ProductId,
        LineId: line.LineId,
        Quantity: line.Quantity,
        UnitOfMeasureSymbol: line.UnitOfMeasureSymbol,
        Comments: line.Comment
    }));

    setCartData({ ...handshake, cartLines, heldOrderHeader: heldOrderHeaderOf(cart) });
};

/** Puts back what the store held, after opening the order recorded the cart's current lines and header in their place. */
export const keepSentLines = (cartId: string, sentLines: ISentLine[], heldOrderHeader?: IHeldOrderHeader): void => {
    const handshake = getCartData();

    if (handshake?.CartId === cartId) {
        setCartData(heldOrderHeader ? { ...handshake, cartLines: sentLines, heldOrderHeader } : { ...handshake, cartLines: sentLines });
    }
};

const isChanged = (line: CartLine, held: ISentLine): boolean =>
    line.Quantity !== held.Quantity || (line.Comment ?? '') !== (held.Comments ?? '');

/**
 * The changes an order update sends: the cart as the guest sees it, measured against the lines the store held.
 * @param cart - This channel's copy of the order.
 * @param sentLines - The lines the store held when the order was last read.
 * @param alreadyRemoved - Lines the store has already taken off, whose removal is not sent again.
 * @param heldOrderHeader - The header values the store held, where recorded.
 * @returns The lines to add, update and remove, and the order note and allergens the guest changed.
 */
export const deriveOrderChanges = (
    cart: Cart,
    sentLines: ISentLine[],
    alreadyRemoved: string[] = [],
    heldOrderHeader?: IHeldOrderHeader
): IOrderChanges => {
    const shown = (cart.CartLines ?? []).filter(line => !line.IsVoided);
    const shownById = new Map(shown.map(line => [line.LineId ?? '', line]));
    const heldIds = new Set(sentLines.map(held => held.LineId ?? ''));

    const AddCartLines = shown
        .filter(line => !heldIds.has(line.LineId ?? ''))
        .map(line => ({
            ItemId: line.ItemId,
            ProductId: line.ProductId,
            InventoryDimensionId: line.InventoryDimensionId,
            Quantity: line.Quantity,
            UnitOfMeasureSymbol: line.UnitOfMeasureSymbol,
            Comments: line.Comment ?? '',
            ExtensionProperties: withoutStoreLineFacts(line.ExtensionProperties)
        }));

    const UpdateCartLines = sentLines
        .map(held => ({ held, line: shownById.get(held.LineId ?? '') }))
        .filter((pair): pair is { held: ISentLine; line: CartLine } => !!pair.line && isChanged(pair.line, pair.held))
        .map(({ line }) => ({
            LineId: line.LineId,
            ItemId: line.ItemId,
            ProductId: line.ProductId,
            InventoryDimensionId: line.InventoryDimensionId,
            Quantity: line.Quantity,
            UnitOfMeasureSymbol: line.UnitOfMeasureSymbol,
            Comments: line.Comment ?? ''
        }));

    const voidedIds = new Set((cart.CartLines ?? []).filter(line => line.IsVoided).map(line => line.LineId ?? ''));
    const RemoveCartLines = sentLines
        .map(held => held.LineId ?? '')
        .filter(lineId => !!lineId && !shownById.has(lineId) && !voidedIds.has(lineId) && !alreadyRemoved.includes(lineId))
        .map(lineId => ({ LineId: lineId }));

    const ExtensionProperties = heldOrderHeader
        ? [...orderNoteChange(cart, heldOrderHeader.Note), ...orderAllergensChange(cart, heldOrderHeader.Allergens)]
        : [];

    return { AddCartLines, UpdateCartLines, RemoveCartLines, ExtensionProperties };
};

export const hasOrderChanges = (changes: IOrderChanges): boolean =>
    changes.AddCartLines.length > 0 ||
    changes.UpdateCartLines.length > 0 ||
    changes.RemoveCartLines.length > 0 ||
    changes.ExtensionProperties.length > 0;
